/**
 * Vista mobile para lista de pedidos N2 con selección
 */
import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Stack,
    Typography,
    Chip,
    Checkbox,
    Divider,
    Button
} from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import Utils from '../../../../utils/Utils';
import OrderStates from '../../../../utils/OrderStates';
import { BADGE_COLORS, getBadgeStyles } from '../../../../components/ui/badgeColors';
import { getOrderStateBadgeStyles, getOrderStateN2BadgeStyles } from '../../utils/orderStateColors';
import EtiquetasPedidoN2 from '../ui/EtiquetasPedidoN2';
import { ORDER_STATE_N2 } from '../../../../config/constants';

/**
 * Lista mobile de pedidos N2 con checkboxes de selección
 * @param {Object} props
 * @param {Array} props.pedidos - Array de pedidos
 * @param {Array} props.seleccionados - IDs de pedidos seleccionados
 * @param {Function} props.onSelectionChange - Callback al cambiar selección
 * @param {Set} props.pedidosEnGrupos - Set de números de pedidos ya en grupos
 * @param {Function} props.getNombreAlmacen - Helper para obtener nombre de almacén
 * @param {Function} [props.onForzar] - Forzar el pedido como preparado (quitarlo de la lista)
 * @param {Object} [props.productosPorPedido] - Productos precargados por `num` de pedido
 *   (useProductosPedidosN2): `undefined` mientras cargan, `null` si fallaron
 */
const PedidosN2MobileList = ({
    pedidos,
    seleccionados,
    onSelectionChange,
    pedidosEnGrupos,
    getNombreAlmacen,
    onForzar,
    productosPorPedido = {}
}) => {
    const handleToggleSelection = (pedidoId, isSelectable) => {
        if (!isSelectable) return;

        const newSelection = seleccionados.includes(pedidoId)
            ? seleccionados.filter(id => id !== pedidoId)
            : [...seleccionados, pedidoId];

        onSelectionChange(newSelection);
    };

    return (
        <Stack spacing={2}>
            {pedidos.map((pedido) => {
                const isExcluido = pedido.exclude;
                const isEnGrupo = pedidosEnGrupos.has(pedido.num);
                // Solo los preparados quedan fuera de la agrupación
                const isNoDisponible = pedido.state === ORDER_STATE_N2.PREPARADO;
                const isSelectable = !isExcluido && !isEnGrupo && !isNoDisponible;
                const isSelected = seleccionados.includes(pedido.id);

                const getBackgroundColor = () => {
                    if (isExcluido) return "#ff943975";
                    if (pedido.urgente) return "#b8daba";
                    if (isEnGrupo || isNoDisponible) return "#f2f4f4";
                    return "#fff";
                };

                return (
                    <Card
                        key={pedido.id}
                        sx={{
                            backgroundColor: getBackgroundColor(),
                            border: isSelected ? "2px solid #cc071e" : "1px solid #dee2e6",
                            opacity: isSelectable ? 1 : 0.7,
                            cursor: isSelectable ? "pointer" : "not-allowed"
                        }}
                        onClick={() => handleToggleSelection(pedido.id, isSelectable)}
                    >
                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                            <Stack direction="row" spacing={1} alignItems="flex-start">
                                {/* Checkbox */}
                                <Checkbox
                                    checked={isSelected}
                                    disabled={!isSelectable}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                        if (isSelectable) {
                                            const newSelection = e.target.checked
                                                ? [...seleccionados, pedido.id]
                                                : seleccionados.filter(id => id !== pedido.id);
                                            onSelectionChange(newSelection);
                                        }
                                    }}
                                    sx={{ p: 0, mt: 0.5 }}
                                />

                                {/* Info del pedido */}
                                <Box sx={{ flex: 1 }}>
                                    {/* Número y tipo */}
                                    <Stack direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
                                        <Typography sx={{ fontWeight: 700, color: "#cc071e", fontSize: { xs: 14, sm: 15 } }}>
                                            {pedido.num}
                                        </Typography>
                                        <Chip
                                            label={Utils.toCapitalize(pedido.type)}
                                            size="small"
                                            sx={getBadgeStyles(BADGE_COLORS.GRAY, { height: 20, fontSize: 10 })}
                                        />
                                    </Stack>

                                    {/* Cliente */}
                                    <Typography sx={{ fontSize: { xs: 12, sm: 13 }, mb: 0.5 }}>
                                        <strong>Cliente:</strong> {Utils.toCapitalize(pedido.customer)}
                                    </Typography>

                                    {/* Ruta y Transporte */}
                                    <Stack direction="row" spacing={0.75} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                                        {pedido.route && (
                                            <Chip
                                                label={`📍 ${Utils.toCapitalize(pedido.route)}`}
                                                size="small"
                                                sx={getBadgeStyles(BADGE_COLORS.BLUE, { height: { xs: 18, sm: 20 }, fontSize: { xs: 9, sm: 10 } })}
                                            />
                                        )}
                                        {pedido.transport && (
                                            <Chip
                                                label={`🚚 ${Utils.toCapitalize(pedido.transport)}`}
                                                size="small"
                                                sx={getBadgeStyles(BADGE_COLORS.GRAY, { height: { xs: 18, sm: 20 }, fontSize: { xs: 9, sm: 10 } })}
                                            />
                                        )}
                                    </Stack>

                                    <Divider sx={{ my: { xs: 0.75, sm: 1 } }} />

                                    {/* Estados */}
                                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                        <Chip
                                            label={`N1: ${OrderStates.getState(pedido.stateN1)}`}
                                            size="small"
                                            sx={getOrderStateBadgeStyles(pedido.stateN1, { height: { xs: 18, sm: 20 }, fontSize: { xs: 9, sm: 10 } })}
                                        />
                                        <Chip
                                            label={`N2: ${OrderStates.getStateN2(pedido.state)}`}
                                            size="small"
                                            sx={getOrderStateN2BadgeStyles(pedido.state, { height: { xs: 18, sm: 20 }, fontSize: { xs: 9, sm: 10 } })}
                                        />
                                        <Chip
                                            label={getNombreAlmacen ? getNombreAlmacen(pedido.warehouse) : pedido.warehouse}
                                            size="small"
                                            sx={getBadgeStyles(BADGE_COLORS.GRAY, { height: { xs: 18, sm: 20 }, fontSize: { xs: 9, sm: 10 } })}
                                        />
                                    </Stack>

                                    {/* Indicadores especiales */}
                                    {isEnGrupo && (
                                        <Typography sx={{ fontSize: { xs: 10, sm: 11 }, color: "#6c757d", fontStyle: "italic", mt: { xs: 0.75, sm: 1 } }}>
                                            Ya está en un grupo
                                        </Typography>
                                    )}
                                    {isNoDisponible && (
                                        <Typography sx={{ fontSize: { xs: 10, sm: 11 }, color: "#6c757d", fontStyle: "italic", mt: { xs: 0.75, sm: 1 } }}>
                                            No disponible (ya preparado)
                                        </Typography>
                                    )}
                                    {isExcluido && (
                                        <Typography sx={{ fontSize: { xs: 10, sm: 11 }, color: BADGE_COLORS.RED.text, fontStyle: "italic", mt: { xs: 0.75, sm: 1 } }}>
                                            ⚠️ Excluido
                                        </Typography>
                                    )}

                                    {/* Etiquetas y forzar: siempre activos, aunque la tarjeta no sea
                                        seleccionable, porque los colgados suelen ser justo los que no lo son */}
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.25 }}>
                                        <EtiquetasPedidoN2 pedido={pedido} productos={productosPorPedido[pedido.num]} />
                                        {onForzar && (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<DoneAllIcon />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onForzar(pedido);
                                                }}
                                                sx={{
                                                    textTransform: "none",
                                                    fontWeight: 600,
                                                    fontSize: 12,
                                                    color: "#495057",
                                                    borderColor: "#ced4da",
                                                    backgroundColor: "#fff",
                                                    cursor: "pointer"
                                                }}
                                            >
                                                Forzar preparado
                                            </Button>
                                        )}
                                    </Stack>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                );
            })}
        </Stack>
    );
};

export default PedidosN2MobileList;
